import { ColorsConfig } from '@ddt-web/config';
import { TabData } from '@ddt-web/data-service';
import { ObjectUtil } from '@ddt-web/generic-utils';
import * as cloneDeep from 'lodash/cloneDeep';
import * as get from 'lodash/get';

import { ChartFactory } from '../factories';
import { DefaultChartConfig } from './default-chart.config';
import { ColumnChartName } from './enums';
import { ChartType } from './enums/chart-type.enum';

declare const Highcharts;

/**
 * INDICATOR_COLUMN_CHART_CONFIG: configuration settings unique to a Indicator Column Chart.
 */
const INDICATOR_COLUMN_CHART_CONFIG: any = {
	chart: {
		marginTop: 0,
		spacingTop: 0,
		marginBottom: 20
	},
	plotOptions: {
		column: {
			minPointLength: 3,
			stacking: 'normal',
			dataLabels: {
				enabled: false
			},
			pointWidth: 20
		},
		series: {
			states: {
				hover: {
					enabled: false
				}
			}
		}
	},
	tooltip: {
		enabled: false
	},
	xAxis: {
		labels: {
			enabled: false
		},
		title: {
			text: null
		},
		minorTickLength: 0,
		tickLength: 0,
		lineWidth: 0,
		minorGridLineWidth: 0,
		lineColor: 'transparent',
		gridLineColor: 'transparent'
	},
	yAxis: {
		labels: {
			enabled: true,
			style: {},
			formatter: function (): any {
				const label = this.axis.defaultLabelFormatter.call(this);
				const tickInterval = this.axis.tickInterval;
				if (!isNaN(label) && tickInterval % 1 !== 0) {
					return Number(label).toFixed(1);
				}
				return label;
			}
		},
		title: {
			text: null
		},
		plotLines: [
			{
				value: 0,
				color: ColorsConfig.CTA_COLOR,
				zIndex: 5,
				width: 2
			}
		],
		opposite: true,
		min: 0,
		tickPixelInterval: 30
	}
};

export class IndicatorColumnChartConfig extends DefaultChartConfig {
	private value;
	private median;

	constructor(
		protected type: ChartType,
		protected name: ColumnChartName,
		protected container: HTMLElement,
		protected data: any,
		protected dataIndex?: number,
		protected colors?: Array<string>,
		protected height?: number,
		protected width?: number,
		protected title?: string,
		protected hideTitle?: boolean,
		protected x?: string,
		protected y?: string,
		protected customAttributes?: any,
		protected opposite: boolean = false
	) {
		super(type, container, colors, height, width, title, hideTitle);
		this.initialize();
	}

	private initialize(): void {
		if (get(this.data, 'tableData[0].cells[0]')) {
			this.value = this.data.tableData[0].cells[this.dataIndex][0].rawValue;
			this.median = this.data?.tableData[0]?.cells[this.dataIndex][1]?.rawValue;
		}
		this.configuration = cloneDeep(ObjectUtil.extend(this.configuration, INDICATOR_COLUMN_CHART_CONFIG));
		this.configuration.chart.type = ChartType.Column;
		// TODO: split component specific logic out of chart configuration
		if (this.data instanceof TabData && this.median !== undefined) {
			// Calculate the median value and display it as a plot line
			this.configuration.yAxis.plotLines[0].value = this.median;
			this.configuration.yAxis.max = Math.max(this.median, this.value);
			this.configuration.series = [{ data: [this.value] }];
		}

		if (this.customAttributes?.hidePlotLine) {
			this.configuration.yAxis.plotLines = [];
		}

		if (this.customAttributes && this.customAttributes.numberOfTicks) {
			if (this.customAttributes.axis === 'yAxis') {
				this.configuration.yAxis.tickAmount = this.customAttributes.numberOfTicks;
				if (this.value && this.median) {
					const topValue = Math.max(this.value, this.median);
					const rawInterval = topValue / (this.customAttributes.numberOfTicks - 1);
					this.configuration.yAxis.tickInterval = Math.ceil(rawInterval * 10) / 10;
				}
				if (this.customAttributes.min) {
					this.configuration.yAxis.min = this.customAttributes.min;
				}
				if (this.customAttributes.max) {
					this.configuration.yAxis.max = this.customAttributes.max;
				}
			} else {
				this.configuration.xAxis.tickAmount = this.customAttributes.numberOfTicks;
				if (this.customAttributes.min) {
					this.configuration.xAxis.min = this.customAttributes.min;
				}
				if (this.customAttributes.max) {
					this.configuration.xAxis.max = this.customAttributes.max;
				}
			}
		}

		if (this.configuration.yAxis) {
			this.configuration.yAxis.opposite = this.opposite;
		}
	}

	public render(): void {
		this.updateDefaultConfiguration();
		if (!this.name) {
			this.renderDefaultChart();
		} else if (this.name === ColumnChartName.Exposure) {
			this.renderExposureChart();
		}
	}

	private updateDefaultConfiguration(): void {
		let marginTop: number = 0;
		let spacingBottom: number = 0;
		const sibling: any = this.container.parentElement.previousElementSibling || this.container.parentElement.nextElementSibling;
		if (sibling && sibling.tagName.toLowerCase() === 'dl-display-table') {
			marginTop = sibling.querySelector('header').offsetHeight + 1;
			spacingBottom = 10;
		}
		this.configuration = ObjectUtil.extend(this.configuration, {
			chart: {
				marginTop: marginTop,
				spacingBottom: spacingBottom
			},
			colors: this.colors ? this.colors : [ColorsConfig.DEFAULT_SEQUENTIAL_COLORS_ORDERING[0]],
			series: [
				{
					data: [this.value]
				}
			]
		});
	}

	private renderDefaultChart(): void {
		/*Set the margin top attribute for the chart*/
		this.configuration.chart.marginTop = 10;
		Highcharts.chart(this.configuration);
	}

	private renderExposureChart(): void {
		Highcharts.chart(
			ObjectUtil.extend(this.configuration, {
				colors: ChartFactory.getExposureColorsForTabData(this.data),
				plotOptions: {
					column: {
						minPointLength: 3,
						stacking: 'percent',
						dataLabels: {
							enabled: true,
							formatter: function (): any {
								return this.y > 0 ? this.series.name : null;
							},
							style: {
								textOutline: 'none',
								fontSize: '12px'
							}
						}
					}
				},
				series: ChartFactory.getExposureColumnSeriesForTabData(this.data)
			})
		);
	}

	public getConfigurationForPDF(): any {
		const configuration = ObjectUtil.extend(this.configuration, INDICATOR_COLUMN_CHART_CONFIG);
		if (this.median !== undefined) {
			configuration.yAxis.plotLines[0].value = this.median;
			configuration.yAxis.max = Math.max(this.median, this.value);
		}

		if (this.customAttributes?.hidePlotLine) {
			configuration.yAxis.plotLines = [];
		}

		configuration.yAxis.tickAmount = this.customAttributes && this.customAttributes.numberOfTicks ? this.customAttributes.numberOfTicks : 4;
		configuration.series = [{ data: [this.value] }];
		configuration.chart.height = this.height ? this.height : 80;
		configuration.chart.marginBottom = 10;
		configuration.chart.marginTop = 10;
		configuration.yAxis.opposite = this.customAttributes && this.customAttributes.opposite === false ? this.customAttributes.opposite : true;
		configuration.chart.width = this.width ? this.width : 80;
		configuration.chart.type = ChartType.Column;
		configuration.colors = this.customAttributes && this.customAttributes.colors ? this.customAttributes.colors : [ColorsConfig.DEFAULT_SEQUENTIAL_COLORS_ORDERING[0]];
		configuration.plotOptions.column.pointWidth = 10;
		configuration.chart.style.fontSize = '8px';
		configuration.yAxis.labels.style.fontSize = '8px';
		return configuration;
	}
}
